import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { api, errMsg } from '../api.js';
import { Link } from 'react-router-dom';
import { toast } from '../components/Toasts.jsx';
import EmptyState from '../components/EmptyState.jsx';
import ConfirmDialog from '../components/ConfirmDialog.jsx';
import { Modal } from '../components/ui';
import { formatDateTime, timeAgo } from '../util/time.js';

const TYPES = [
  { value: 'cv', label: 'CV' },
  { value: 'cover-letter', label: 'Cover letter' },
  { value: 'linkedin-pdf', label: 'LinkedIn PDF' },
  { value: 'linkedin-export', label: 'LinkedIn export (ZIP)' },
  { value: 'job-description', label: 'Job description' },
  { value: 'interview-note', label: 'Interview note' },
  { value: 'feedback', label: 'Feedback' },
  { value: 'other', label: 'Other' },
];

export default function DocumentsPage() {
  const [type, setType] = useState('cv');
  const [docs, setDocs] = useState([]);
  const [activeCvId, setActiveCvId] = useState(null); // T024
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [diffPair, setDiffPair] = useState(null); // T024 — { aId, bId } pending diff fetch
  const [diffResult, setDiffResult] = useState(null); // T024 — fetched diff payload
  const [diffLoading, setDiffLoading] = useState(false);
  const fileInput = useRef(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [docsBody, profileBody] = await Promise.all([
        // T183 — via api.js, not raw fetch: only api.js dispatches
        // `hunter:unauthorized` on a 401, so with a bare fetch an expired
        // session left this page showing stale documents forever instead of
        // bouncing to /login like every other page.
        api.get('/documents'),
        // T024 — get the active-CV pointer. Profile may not exist yet
        // for first-time users; the endpoint returns an empty shape.
        api.get('/profile').catch(() => null),
      ]);
      setDocs(docsBody.documents ?? []);
      setActiveCvId(profileBody?.profile?.activeCvDocumentId ?? null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // T024 — Set a CV as active.
  const onSetActive = useCallback(
    async (id) => {
      try {
        const r = await api.patch(`/documents/${id}/set-active`, {});
        setActiveCvId(r.activeCvDocumentId);
        toast('Active CV updated', { kind: 'success' });
      } catch (e) {
        toast(e.body?.message || e.message, { kind: 'error' });
      }
    },
    [],
  );

  // T024 — Open the diff modal between two selected CVs.
  const openDiff = useCallback(async (aId, bId) => {
    setDiffPair({ aId, bId });
    setDiffLoading(true);
    setDiffResult(null);
    try {
      const r = await api.get(`/documents/diff?aId=${aId}&bId=${bId}`);
      setDiffResult(r);
    } catch (e) {
      toast(e.body?.message || e.message, { kind: 'error' });
      setDiffPair(null);
    } finally {
      setDiffLoading(false);
    }
  }, []);

  // T024 — CVs only, newest first. Used to seed the diff dropdown.
  const cvDocs = useMemo(() => docs.filter((d) => d.type === 'cv'), [docs]);

  // T023 — Library filters: type tab + filename search.
  const [filterType, setFilterType] = useState('all'); // 'all' | one of DOCUMENT_TYPES
  const [searchQ, setSearchQ] = useState('');

  // Counts per type, including a synthetic "all" bucket. Drives the tab
  // labels so the operator sees how much they have without clicking.
  const countsByType = useMemo(() => {
    const c = { all: docs.length };
    for (const d of docs) c[d.type] = (c[d.type] ?? 0) + 1;
    return c;
  }, [docs]);

  // The actual rendered slice — applies the active tab + search.
  const filteredDocs = useMemo(() => {
    const q = searchQ.trim().toLowerCase();
    return docs.filter((d) => {
      if (filterType !== 'all' && d.type !== filterType) return false;
      if (q && !d.originalFilename.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [docs, filterType, searchQ]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const onUpload = useCallback(
    async (file) => {
      if (!file) return;
      setBusy(true);
      setError(null);
      try {
        // T266(5) — api.js can carry multipart now (it omits Content-Type so the
        // browser sets the boundary), which is what forced this raw fetch and
        // its hand-copied 401 branch. One client, one 401 rule, one error shape.
        await api.upload('/documents', file, { type });
        // Only the parseable types have a next step in Profile.
        setLastUploadedType(/^(cv|linkedin-)/.test(type) ? type : null);
        await refresh();
      } catch (e) {
        setError(errMsg(e, 'Upload failed'));
      } finally {
        setBusy(false);
      }
    },
    [type, refresh],
  );

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) onUpload(file);
  };

  const [lastUploadedType, setLastUploadedType] = useState(null);
  const [deleteDocId, setDeleteDocId] = useState(null);

  const onDelete = async (id) => {
    try {
      await api.delete(`/documents/${id}`); // T183 — see the note in refresh()
      await refresh();
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <>
      <header className="border-b border-slate-800 pb-6">
        <h1 className="text-3xl font-semibold tracking-tight text-brand-500">Documents</h1>
        <p className="mt-1 text-sm text-slate-400">
          {docs.length === 0
            ? 'CV · LinkedIn · cover letters · interview notes'
            : `${docs.length} document${docs.length === 1 ? '' : 's'} · ${countsByType.cv ?? 0} CV${(countsByType.cv ?? 0) === 1 ? '' : 's'}`}
        </p>
      </header>

      <section className="mt-8 grid gap-4 sm:grid-cols-[1fr_2fr]">
        <div>
          <label className="block">
            <span className="text-xs uppercase tracking-wide text-slate-400">Document type</span>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="mt-1 w-full rounded-md border border-slate-700 bg-slate-950/40 px-3 py-2 text-sm text-slate-100 focus:border-brand-500 focus:outline-none"
            >
              {TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </label>
          <p className="mt-3 text-xs text-slate-500">
            Allowed: PDF · ZIP · TXT · PNG · JPEG. Max 25&nbsp;MB. Duplicate uploads of the same file
            return the existing document — no churn.
          </p>
        </div>

        <label
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={
            'flex h-32 cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed transition focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-600 ' +
            (dragOver
              ? 'border-brand-500 bg-brand-500/10 text-brand-500'
              : 'border-slate-700 text-slate-400 hover:border-brand-500 hover:text-brand-500')
          }
        >
          <span className="text-sm">
            {busy ? 'Uploading…' : dragOver ? 'drop to upload' : 'drag a file here or click to browse'}
          </span>
          <span className="mt-1 text-xs text-slate-500">type = {type}</span>
          {/* sr-only, not hidden: display:none takes the input out of the tab
              order and the a11y tree, so the only way to upload was a mouse. A
              <label> wrapper gives Enter/Space for free — no key handler,
              no synthetic role. */}
          <input
            ref={fileInput}
            type="file"
            className="sr-only"
            aria-label={`Upload a ${type} document`}
            onChange={(e) => onUpload(e.target.files?.[0])}
          />
        </label>
      </section>

      {/* T267(7) — uploading a CV here and parsing it in Profile are two halves
          of one job, and this page never said so: the operator uploaded, saw a
          row appear, and had no indication that anything else was needed for
          it to affect his scoring. */}
      {lastUploadedType && (
        <p className="mt-4 rounded-md border border-brand-700 bg-brand-600/5 p-3 text-xs text-brand-300">
          Uploaded. It does not affect your scoring until it is parsed —{' '}
          <Link to="/profile" className="underline decoration-dotted hover:text-brand-400">
            import it from Profile → Import
          </Link>
          .
        </p>
      )}

      {error && (
        <p className="mt-4 rounded border border-red-800 bg-red-950/40 p-3 text-sm text-red-300">
          {error}
        </p>
      )}

      <section className="mt-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-xs uppercase tracking-wide text-slate-500">Library</h2>
          <input
            type="search"
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            placeholder="search filenames…"
            className="w-full max-w-xs rounded-md border border-slate-700 bg-slate-950/40 px-3 py-1 text-xs text-slate-100 focus:border-brand-500 focus:outline-none sm:w-64"
          />
        </div>

        {/* T023 — type tabs with counts so the operator sees the library shape at a glance */}
        <div className="mt-3 flex flex-wrap items-center gap-1.5 border-b border-slate-800 pb-2">
          <FilterTab
            label="All"
            active={filterType === 'all'}
            count={countsByType.all ?? 0}
            onClick={() => setFilterType('all')}
          />
          {TYPES.map((t) => {
            const n = countsByType[t.value] ?? 0;
            if (n === 0 && filterType !== t.value) return null; // hide empty tabs unless selected
            return (
              <FilterTab
                key={t.value}
                label={t.label}
                active={filterType === t.value}
                count={n}
                onClick={() => setFilterType(t.value)}
              />
            );
          })}
          {(filterType !== 'all' || searchQ) && (
            <button
              type="button"
              onClick={() => {
                setFilterType('all');
                setSearchQ('');
              }}
              className="ml-2 text-xs text-slate-500 underline decoration-dotted hover:text-brand-500"
            >
              clear filters
            </button>
          )}
        </div>

        {loading ? (
          <p className="mt-3 text-sm text-slate-500">Loading…</p>
        ) : docs.length === 0 ? (
          <EmptyState
            icon="⛁"
            title="No documents yet"
            body="Upload your CV, a LinkedIn export, or a cover letter to feed the AI features."
            actions={[
              { label: 'Upload a CV', onClick: () => fileInput.current?.click(), primary: true },
            ]}
          />
        ) : filteredDocs.length === 0 ? (
          <EmptyState
            icon="◌"
            title="No documents match your filters"
            body="Try a different type or clear the search."
            actions={[
              {
                label: 'Clear filters',
                onClick: () => { setFilterType('all'); setSearchQ(''); },
              },
            ]}
          />
        ) : (
          <>
          <table className="mt-3 hidden w-full text-left text-sm md:table">
            <thead className="border-b border-slate-800 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="py-2 pr-4">Type</th>
                <th className="py-2 pr-4">v</th>
                <th className="py-2 pr-4">File</th>
                <th className="py-2 pr-4">Size</th>
                <th className="py-2 pr-4">Uploaded</th>
                <th className="py-2 pr-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredDocs.map((d) => {
                const isActiveCv = d.type === 'cv' && d.id === activeCvId;
                const otherCvs = d.type === 'cv' ? cvDocs.filter((o) => o.id !== d.id) : [];
                return (
                  <tr key={d.id} className="border-b border-slate-900">
                    <td className="py-2 pr-4 text-slate-300">{d.type}</td>
                    <td className="py-2 pr-4 text-slate-400">
                      v{d.version}
                      {isActiveCv && (
                        <span className="ml-2 rounded border border-emerald-700 bg-emerald-950/30 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-emerald-300">
                          Active
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-4 text-slate-200">
                      <a
                        href={`/api/documents/${d.id}/raw`}
                        target="_blank"
                        rel="noreferrer"
                        className="underline decoration-dotted hover:text-brand-500"
                      >
                        {d.originalFilename}
                      </a>
                    </td>
                    <td className="py-2 pr-4 text-slate-400">{formatBytes(d.sizeBytes)}</td>
                    <td className="py-2 pr-4 text-slate-500" title={formatDateTime(d.createdAt)}>
                      {timeAgo(d.createdAt)}
                    </td>
                    <td className="py-2 pr-4 text-right">
                      <div className="flex flex-wrap items-center justify-end gap-2">
                        {d.type === 'cv' && !isActiveCv && (
                          <button
                            type="button"
                            onClick={() => onSetActive(d.id)}
                            className="text-xs text-slate-400 underline decoration-dotted hover:text-emerald-300"
                            title="Use this CV for new applications"
                          >
                            make active
                          </button>
                        )}
                        {d.type === 'cv' && otherCvs.length > 0 && (
                          <select
                            value=""
                            onChange={(e) => {
                              const otherId = e.target.value;
                              if (otherId) openDiff(d.id, otherId);
                            }}
                            className="rounded border border-slate-700 bg-slate-950/40 px-1.5 py-0.5 text-xs text-slate-400 focus:border-brand-500 focus:outline-none"
                            title="Compare this CV against another version"
                          >
                            <option value="">diff vs…</option>
                            {otherCvs.map((o) => (
                              <option key={o.id} value={o.id}>
                                v{o.version} · {o.originalFilename}
                              </option>
                            ))}
                          </select>
                        )}
                        <button
                          type="button"
                          onClick={() => setDeleteDocId(d.id)}
                          className="text-xs text-slate-500 underline decoration-dotted hover:text-red-400"
                        >
                          delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="mt-3 space-y-2 md:hidden">
            {filteredDocs.map((d) => {
              const isActiveCv = d.type === 'cv' && d.id === activeCvId;
              const otherCvs = d.type === 'cv' ? cvDocs.filter((o) => o.id !== d.id) : [];
              return (
                <DocCard
                  key={d.id}
                  d={d}
                  isActiveCv={isActiveCv}
                  otherCvs={otherCvs}
                  onSetActive={onSetActive}
                  onRequestDelete={setDeleteDocId}
                  openDiff={openDiff}
                />
              );
            })}
          </div>
          </>
        )}
      </section>

      {diffPair && (
        <DiffModal
          loading={diffLoading}
          result={diffResult}
          onClose={() => {
            setDiffPair(null);
            setDiffResult(null);
          }}
        />
      )}

      {deleteDocId && (
        <ConfirmDialog
          title="Remove this document?"
          message="It's hidden from your library but recoverable in the database. Applications that already saved a copy of it keep working."
          confirmLabel="Remove"
          busyLabel="removing…"
          onCancel={() => setDeleteDocId(null)}
          onConfirm={async () => {
            await onDelete(deleteDocId);
            setDeleteDocId(null);
          }}
        />
      )}
    </>
  );
}

function DiffModal({ loading, result, onClose }) {
  // T131-fu — built on the shared <Modal> (bare mode, size xl, scroll): gains a
  // focus trap; Modal owns the backdrop + Escape.
  return (
    <Modal role="dialog" size="xl" scroll labelledById="diff-title" onClose={onClose}>
        <header className="flex items-start justify-between gap-3 border-b border-slate-800 p-4">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wide text-brand-500">CV diff</p>
            <h2 id="diff-title" className="mt-0.5 text-base font-semibold text-slate-100">
              {loading
                ? 'Comparing CV versions…'
                : result
                  ? `v${result.a.version} → v${result.b.version}`
                  : 'CV diff'}
            </h2>
            {result && (
              <p className="mt-0.5 text-xs text-slate-500">
                <span className="text-red-400">−{result.diff.stats.removed}</span>{' '}
                <span className="text-emerald-400">+{result.diff.stats.added}</span>{' '}
                <span className="text-slate-600">·{result.diff.stats.context} unchanged</span>
                {(result.diff.truncatedA || result.diff.truncatedB) && (
                  <span className="ml-2 text-amber-400">⚠ truncated at 2000 lines per side</span>
                )}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md border border-slate-700 px-2 py-1 text-xs text-slate-300 hover:border-slate-500 hover:text-slate-200"
          >
            ✕
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-4">
          {loading && <p className="text-sm text-slate-500">loading diff…</p>}
          {result && result.diff.lines.length === 0 && (
            <p className="text-sm text-slate-500">No textual difference between these CVs.</p>
          )}
          {result && result.diff.lines.length > 0 && (
            <pre className="overflow-x-auto rounded-md border border-slate-800 bg-slate-950/40 p-3 font-mono text-xs leading-relaxed">
              {result.diff.lines.map((l, i) => (
                <div
                  key={i}
                  className={
                    l.type === 'add'
                      ? 'whitespace-pre-wrap text-emerald-300'
                      : l.type === 'remove'
                        ? 'whitespace-pre-wrap text-red-400'
                        : 'whitespace-pre-wrap text-slate-400'
                  }
                >
                  <span className="select-none pr-2">{l.type === 'add' ? '+' : l.type === 'remove' ? '−' : ' '}</span>
                  {l.text || ' '}
                </div>
              ))}
            </pre>
          )}
        </div>
    </Modal>
  );
}

function formatBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

// T122 — mobile card for a document row (the table reflowed). Reuses the exact
// make-active / diff-vs / delete controls; isActiveCv/otherCvs are computed once
// per doc by the caller and passed in so the card can't diverge from the table.
function DocCard({ d, isActiveCv, otherCvs, onSetActive, onRequestDelete, openDiff }) {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-3 text-sm">
      <div className="flex items-start justify-between gap-2">
        <a
          href={`/api/documents/${d.id}/raw`}
          target="_blank"
          rel="noreferrer"
          className="min-w-0 truncate text-slate-200 underline decoration-dotted hover:text-brand-500"
        >
          {d.originalFilename}
        </a>
        {isActiveCv && (
          <span className="shrink-0 rounded border border-emerald-700 bg-emerald-950/30 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-emerald-300">
            Active
          </span>
        )}
      </div>
      <div className="mt-1 text-xs text-slate-500">
        {d.type} · v{d.version} · {formatBytes(d.sizeBytes)} ·{' '}
        <span title={formatDateTime(d.createdAt)}>{timeAgo(d.createdAt)}</span>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {d.type === 'cv' && !isActiveCv && (
          <button
            type="button"
            onClick={() => onSetActive(d.id)}
            className="text-xs text-slate-400 underline decoration-dotted hover:text-emerald-300"
          >
            make active
          </button>
        )}
        {d.type === 'cv' && otherCvs.length > 0 && (
          <select
            value=""
            onChange={(e) => {
              const otherId = e.target.value;
              if (otherId) openDiff(d.id, otherId);
            }}
            className="rounded border border-slate-700 bg-slate-950/40 px-1.5 py-0.5 text-xs text-slate-400 focus:border-brand-500 focus:outline-none"
          >
            <option value="">diff vs…</option>
            {otherCvs.map((o) => (
              <option key={o.id} value={o.id}>
                v{o.version} · {o.originalFilename}
              </option>
            ))}
          </select>
        )}
        <button
          type="button"
          onClick={() => onRequestDelete(d.id)}
          className="text-xs text-slate-500 underline decoration-dotted hover:text-red-400"
        >
          delete
        </button>
      </div>
    </div>
  );
}

function FilterTab({ label, count, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'rounded-full border px-2.5 py-0.5 text-xs transition ' +
        (active
          ? 'border-brand-500 bg-brand-600/15 text-brand-400'
          : 'border-slate-700 text-slate-400 hover:border-brand-600 hover:text-brand-400')
      }
    >
      {label}
      <span className={'ml-1.5 text-[10px] ' + (active ? 'text-brand-300' : 'text-slate-500')}>{count}</span>
    </button>
  );
}
